import { useEffect, useMemo, useState } from 'react';
import { Folder, Inbox, Layers, Search } from 'lucide-react';
import { placementPath, type Board, type BoardPlacement } from '@kaba/shared';
import { moveBoard, samePlace, targetPath, targetsFor, useNav, type Target } from '../../lib/structure';
import { Button, cx, Sheet } from '../ui/ui';

type Movable = Pick<Board, 'id' | 'title' | 'teamId'> & { placement?: BoardPlacement };

/**
 * „Verschieben nach …“ (B02, M01): aktueller Ort, Suche, nur zulässige Ziele, voller Zielpfad vor der
 * Bestätigung. Am Handy bildschirmfüllend. Ziehen ist nur eine Abkürzung für dieselbe Aktion.
 */
export function MoveBoardDialog({ board, onClose }: { board: Movable | null; onClose: () => void }) {
  return (
    <Sheet open={!!board} onOpenChange={(o) => !o && onClose()} title={board ? `„${board.title}“ verschieben` : 'Verschieben'} side="center">
      {board && <Body board={board} onClose={onClose} />}
    </Sheet>
  );
}

function Body({ board, onClose }: { board: Movable; onClose: () => void }) {
  const { data: nav } = useNav();
  const [q, setQ] = useState('');
  const [target, setTarget] = useState<Target | null>(null);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(board);
  useEffect(() => setCurrent(board), [board]);
  const areas = targetsFor(nav, current.teamId);
  const term = q.trim().toLowerCase();

  const options = useMemo(() => {
    const all: { t: Target; label: string; sub?: string; kind: 'none' | 'area' | 'folder' }[] = [
      { t: { areaId: null, folderId: null }, label: 'Nicht einsortiert', kind: 'none' },
    ];
    for (const a of areas) {
      all.push({ t: { areaId: a.id, folderId: null }, label: a.name, sub: 'direkt im Bereich', kind: 'area' });
      for (const f of a.folders) all.push({ t: { areaId: a.id, folderId: f.id }, label: f.name, sub: a.name, kind: 'folder' });
    }
    return term ? all.filter((o) => `${o.label} ${o.sub ?? ''}`.toLowerCase().includes(term)) : all;
  }, [areas, term]);

  const key = (t: Target) => `${t.areaId ?? '-'}/${t.folderId ?? '-'}`;
  const chosen = target && !samePlace(current.placement, target) ? target : null;

  return (
    <form
      className="flex min-h-full flex-col"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!chosen) return;
        setBusy(true);
        const moved = await moveBoard(current, chosen, {
          // Zwischenzeitlich geändert: Dialog bleibt offen mit neuem Stand und neuer Zielauswahl (M05)
          onConflict: () => setTarget(null),
        });
        setBusy(false);
        if (moved) onClose();
      }}
    >
      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm">
          <span className="text-[var(--ink-soft)]">Aktueller Ort: </span>
          <span className="font-medium">{placementPath(current.placement)}</span>
        </p>
        {!current.teamId ? (
          <p className="rounded-lg bg-black/5 p-3 text-sm text-[var(--ink-soft)] dark:bg-white/5">
            Dieses Board gehört zu keinem Team. In Bereiche lassen sich nur Boards eines Teams einsortieren – übernimm es zuerst in den Teameinstellungen unter
            „Allgemein“ ins Team.
          </p>
        ) : (
          areas.length > 3 && (
            <label className="relative">
              <span className="sr-only">Ziel suchen</span>
              <Search size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[var(--ink-soft)]" aria-hidden />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Bereich oder Ordner suchen …"
                className="min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] pr-2 pl-9 text-base sm:min-h-10 sm:text-sm"
              />
            </label>
          )
        )}
        <fieldset>
          <legend className="mb-1.5 text-sm font-medium">Ziel</legend>
          <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]">
            {options.map((o) => {
              const here = samePlace(current.placement, o.t);
              const Icon = o.kind === 'none' ? Inbox : o.kind === 'area' ? Layers : Folder;
              return (
                <li key={key(o.t)}>
                  <label
                    className={cx(
                      'flex min-h-12 cursor-pointer items-center gap-3 px-3 py-2 text-sm',
                      o.kind === 'folder' && 'pl-8',
                      here && 'cursor-default opacity-60',
                      target && key(target) === key(o.t) && 'bg-accent-soft/50',
                    )}
                  >
                    <input
                      type="radio"
                      name="move-target"
                      className="size-4 accent-[var(--color-accent)]"
                      disabled={here}
                      checked={!!target && key(target) === key(o.t)}
                      onChange={() => setTarget(o.t)}
                    />
                    <Icon size={16} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block break-words">{o.label}</span>
                      {o.sub && <span className="block text-xs text-[var(--ink-soft)]">{o.sub}</span>}
                    </span>
                    {here && <span className="text-xs text-[var(--ink-soft)]">aktueller Ort</span>}
                  </label>
                </li>
              );
            })}
            {!options.length && <li className="p-3 text-sm text-[var(--ink-soft)]">Kein passendes Ziel.</li>}
          </ul>
        </fieldset>
        <p className="text-sm" role="status">
          {chosen ? (
            <>
              <span className="text-[var(--ink-soft)]">Neuer Ort: </span>
              <span className="font-medium">{targetPath(nav, chosen)}</span>
            </>
          ) : (
            <span className="text-[var(--ink-soft)]">Wähle ein Ziel.</span>
          )}
        </p>
        <p className="text-xs text-[var(--ink-soft)]">
          Zugriffsrechte bleiben unverändert. Bereiche ordnen nur die Ablage – wer ein Board sieht, regeln die Freigaben.
        </p>
      </div>
      <div className="pb-safe mt-auto flex justify-end gap-2 border-t border-[var(--line)] p-3">
        <Button type="button" onClick={onClose}>
          Abbrechen
        </Button>
        <Button type="submit" variant="primary" disabled={!chosen || busy}>
          Verschieben
        </Button>
      </div>
    </form>
  );
}
